import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import EventCard from "@/components/feature/EventCard";
import { eventList } from "@/mocks/events";
import { categoryList } from "@/mocks/site";
import EventsHero from "./components/EventsHero";
import EventFilters from "./components/EventFilters";

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("cat") ?? "all";
  const urlQuery = searchParams.get("q") ?? "";
  const [query, setQuery] = useState(urlQuery);
  const [status, setStatus] = useState("all");

  const activeCategoryName =
    categoryList.find((c) => c.key === category)?.name ?? "全部方向";

  useSeo({
    title: "事件追踪总览 - 热点事件时间线与进展盘点 | 爆料网上",
    description:
      "浏览爆料网上收录的全部热点事件，按社会民生、商业消费、科技数码等方向筛选，查看每起事件从发生到最新进展的完整时间线。",
    keywords: "爆料网上事件,事件追踪,热点事件时间线,事件进展盘点,爆料网上事件盘点",
    path: "/events",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "事件追踪总览",
        url: "https://huahuoyouxi.cn/events",
        inLanguage: "zh-CN",
        description: "按方向与状态筛选的热点事件库，每起事件均附完整时间线。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://huahuoyouxi.cn/" },
          { "@type": "ListItem", position: 2, name: "事件追踪", item: "https://huahuoyouxi.cn/events" },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return eventList.filter((item) => {
      const matchCategory = category === "all" || item.category === category;
      const matchStatus = status === "all" || item.status === status;
      const matchQuery =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchStatus && matchQuery;
    });
  }, [category, status, query]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const handleCategory = (value: string) => {
    updateParam("cat", value);
  };

  const resetFilters = () => {
    setQuery("");
    setStatus("all");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <>
      <EventsHero />

      <section className="w-full bg-background-50 border-b border-background-200">
        <EventFilters
          category={category}
          status={status}
          query={query}
          onCategoryChange={handleCategory}
          onStatusChange={setStatus}
          onQueryChange={setQuery}
        />
      </section>

      <section className="w-full px-5 md:px-10 py-10 md:py-14 bg-background-50">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
          <p className="text-sm text-foreground-600">
            当前方向：<span className="text-foreground-950 font-medium">{activeCategoryName}</span>
            <span className="mx-2 text-foreground-300">|</span>
            共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 起事件
          </p>
          {(category !== "all" || status !== "all" || query) && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((item) => (
              <EventCard key={item.id} event={item} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl">
              <i className="ri-search-eye-line" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              没有匹配的事件
            </h2>
            <p className="mt-2 text-sm text-foreground-600 max-w-md">
              试着换个关键词，或把筛选条件放宽一些。也可以直接看看全部事件。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-list-check-2" />
              查看全部事件
            </button>
          </div>
        )}
      </section>
    </>
  );
}